import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { PAGE_CONTAINER_MAX_WIDTH, documentPageContainerSx } from '../constants/documentTheme';

/**
 * Shown when a trip link doesn't open for the signed-in account. Trips are
 * shared with an email address, so the usual cause is being signed in with a
 * different one; say which account is active and offer to switch.
 */
const TripAccessDenied = ({ tripId }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const signedInEmail = user?.email || '';

  const handleSwitchAccount = async () => {
    setSwitching(true);
    try {
      await logout();
    } finally {
      navigate(`/login?next=${encodeURIComponent(`/trips/${tripId}`)}`, { replace: true });
    }
  };

  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
      <Alert severity="warning" data-testid="trip-access-denied" sx={{ borderRadius: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('trip.accessDenied.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {signedInEmail
            ? t('trip.accessDenied.signedInAs', { email: signedInEmail })
            : t('trip.accessDenied.notShared')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('trip.accessDenied.hint')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {signedInEmail && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSwitchAccount}
              disabled={switching}
              data-testid="trip-access-switch-account"
            >
              {t('trip.accessDenied.switchAccount')}
            </Button>
          )}
          <Button variant="outlined" size="small" component={RouterLink} to="/trips">
            {t('trip.accessDenied.goToMyTrips')}
          </Button>
        </Box>
      </Alert>
    </Container>
  );
};

export default TripAccessDenied;
